'use client';

import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface NewEmployeeModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  existingDepartments: string[];
}

const EMPLOYMENT_TYPES = [
  { key: 'full_time', label: 'Full-Time' },
  { key: 'part_time', label: 'Part-Time' },
  { key: 'contractor', label: 'Contractor' },
  { key: 'seasonal', label: 'Seasonal' },
  { key: 'intern', label: 'Intern' },
];

const NewEmployeeModal = ({ open, onClose, onCreated, existingDepartments }: NewEmployeeModalProps) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [department, setDepartment] = useState('');
  const [customDepartment, setCustomDepartment] = useState('');
  const [employmentType, setEmploymentType] = useState('full_time');
  const [hireDate, setHireDate] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const supabase = createClient();
  const { currentUser } = useAuth();

  useEffect(() => {
    if (open) {
      setSubmitting(false);
      setFirstName(''); setLastName(''); setEmail(''); setPhone('');
      setJobTitle(''); setDepartment(existingDepartments[0] ?? ''); setCustomDepartment('');
      setEmploymentType('full_time'); setHireDate(new Date().toISOString().slice(0, 10));
    }
  }, [open, existingDepartments]);

  useEffect(() => {
    if (!open) return;
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [open, onClose]);

  if (!open) return null;

  const finalDepartment = department === '__custom__' ? customDepartment.trim() : department;
  const isValid = firstName.trim().length >= 1 && lastName.trim().length >= 1 && jobTitle.trim().length >= 2 && finalDepartment.length >= 2 && !!hireDate && !submitting;

  const handleCreate = async () => {
    if (!isValid) return;
    setSubmitting(true);

    const payload: any = {
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      email: email.trim() || null,
      phone: phone.trim() || null,
      job_title: jobTitle.trim(),
      department: finalDepartment,
      roles: [],
      employment_type: employmentType,
      hire_date: hireDate,
      status: 'offline',
    };

    console.log('[NewEmployee] Creating:', payload);

    let createdEmployeeId: string | null = null;

    for (let attempt = 0; attempt < 3; attempt++) {
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error(`Save timed out (attempt ${attempt + 1}/3)`)), 8000));

      const opPromise = supabase.from('employees').insert(payload).select('id')
        .then((res: any) => {
          if (res.error) throw res.error;
          if (!res.data || res.data.length === 0) throw new Error('Insert returned no rows');
          return res.data[0];  // first (and only) inserted row
        });

      try {
        const newEmployee = await Promise.race([opPromise, timeoutPromise]);
        createdEmployeeId = (newEmployee as any).id;
        console.log(`[NewEmployee] Created attempt ${attempt + 1} — id: ${createdEmployeeId}`);
        break;
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        const isRateLimit = msg.includes('429') || msg.toLowerCase().includes('too many');
        const isTimeout = msg.includes('timed out');
        console.warn(`[NewEmployee] Attempt ${attempt + 1} failed: ${msg}`);
        if (attempt === 2 || (!isRateLimit && !isTimeout)) {
          toast.error(`Create failed: ${msg}`, { duration: 6000 });
          setSubmitting(false);
          return;
        }
        await new Promise(r => setTimeout(r, 500 * Math.pow(2, attempt)));
      }
    }

    if (!createdEmployeeId) {
      setSubmitting(false);
      return;
    }

    // Seed default time-off balances + initial compensation entry — best-effort
    try {
      const approverName = `${currentUser?.first_name ?? ''} ${currentUser?.last_name ?? ''}`.trim() || 'Unknown';

      await supabase.from('employee_time_off_balances').insert([
        { employee_id: createdEmployeeId, balance_type: 'pto',  hours_accrued: 80, hours_used: 0, hours_per_year: 120, carryover_limit_hours: 40 },
        { employee_id: createdEmployeeId, balance_type: 'sick', hours_accrued: 40, hours_used: 0, hours_per_year: 64,  carryover_limit_hours: 64 },
      ]);
      await new Promise(r => setTimeout(r, 100));
      await supabase.from('employee_compensation_history').insert({
        employee_id: createdEmployeeId,
        effective_date: hireDate,
        salary_band: null,
        pay_type: employmentType === 'contractor' ? 'contractor' : 'salary',
        change_reason: 'Initial hire',
        approved_by_name: approverName,
      });
      console.log('[NewEmployee] Default balances + comp entry seeded');
    } catch (seedErr) {
      console.warn('[NewEmployee] Default seed failed (non-fatal — employee was still created):', seedErr);
    }

    toast.success(`${firstName.trim()} ${lastName.trim()} added`, { duration: 3000, icon: '✨' });
    onCreated();
    setSubmitting(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[120] flex items-center justify-center p-4" onClick={onClose} role="dialog" aria-modal="true">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between p-5 border-b border-gray-100">
          <div>
            <h3 className="text-lg font-bold text-[#1C2E5E]">Add New Employee</h3>
            <p className="text-[12px] text-gray-500 mt-0.5">Quick intake — full HR record can be edited after</p>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18M6 6l12 12" strokeLinecap="round"/></svg>
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-semibold text-gray-700 mb-1.5">First Name <span className="text-red-500">*</span></label>
              <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} className="w-full px-3 py-2 text-[12px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none transition-all" />
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-gray-700 mb-1.5">Last Name <span className="text-red-500">*</span></label>
              <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} className="w-full px-3 py-2 text-[12px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none transition-all" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-semibold text-gray-700 mb-1.5">Email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="optional" className="w-full px-3 py-2 text-[12px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none transition-all" />
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-gray-700 mb-1.5">Phone</label>
              <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="optional" className="w-full px-3 py-2 text-[12px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none transition-all" />
            </div>
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-gray-700 mb-1.5">Job Title <span className="text-red-500">*</span></label>
            <input type="text" value={jobTitle} onChange={e => setJobTitle(e.target.value)} placeholder="e.g. Senior Project Manager" className="w-full px-3 py-2 text-[12px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none transition-all" />
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-gray-700 mb-1.5">Department <span className="text-red-500">*</span></label>
            <select value={department} onChange={e => setDepartment(e.target.value)} className="w-full px-3 py-2 text-[12px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none transition-all">
              {existingDepartments.map(d => <option key={d} value={d}>{d}</option>)}
              <option value="__custom__">+ Add new department…</option>
            </select>
            {department === '__custom__' && (
              <input type="text" value={customDepartment} onChange={e => setCustomDepartment(e.target.value)} placeholder="New department name" maxLength={60} className="w-full mt-2 px-3 py-2 text-[12px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none transition-all" />
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-semibold text-gray-700 mb-1.5">Employment Type</label>
              <select value={employmentType} onChange={e => setEmploymentType(e.target.value)} className="w-full px-3 py-2 text-[12px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none transition-all">
                {EMPLOYMENT_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-gray-700 mb-1.5">Hire Date <span className="text-red-500">*</span></label>
              <input type="date" value={hireDate} onChange={e => setHireDate(e.target.value)} className="w-full px-3 py-2 text-[12px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none transition-all" />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 p-5 border-t border-gray-100">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-[13px] font-semibold text-gray-600 hover:bg-gray-100">Cancel</button>
          <button type="button" onClick={handleCreate} disabled={!isValid} className="px-4 py-2 rounded-lg text-[13px] font-semibold text-white bg-[#1C2E5E] hover:bg-[#022b80] disabled:opacity-50 disabled:cursor-not-allowed">
            {submitting ? 'Creating…' : 'Create Employee'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewEmployeeModal;
